import { createStore } from 'vuex'
import { dbGet, dbPatch, dbPut, hatPath, resolveHatKey } from './db.js'
import { buildMirrorFeed } from '../assets/javascript/mirrorFeed.js'
import { readHatCache, writeHatCache, clearHatCache, hatCacheEmail } from '../assets/javascript/hatCache.js'
import { initializeApp } from "firebase/app";
import { siteUserState, mayRequestMovies, mayApprove, buildSiteUser, siteUserPath } from "../utils/siteAccess.js";
import { isOwner } from "../assets/javascript/owner.mjs";
import { isSignInDismissal, signInMessage } from "../utils/signIn.js";
import { getAuth, GoogleAuthProvider, signInWithPopup, signInWithCustomToken, onAuthStateChanged } from "firebase/auth";

// Firebase
const firebaseConfig = {
  apiKey: "AIzaSyDlfyRC1BgoQ6UCPKsX-dvFC9HumeEwGjg",
    // movie-hat.com, not movie-hat-9c418.firebaseapp.com (2026-10-08): the sign-in popup lands on
  // /__/auth/handler, and served from another site iOS Safari's storage
  // partitioning could strip it of its session state ("missing initial
  // state"). CloudFront proxies /__/auth/* to the Firebase handler, so the
  // flow is first-party; the OAuth client lists this domain's handler.
  authDomain: "movie-hat.com",
  databaseURL: "https://movie-hat-9c418-default-rtdb.firebaseio.com",
  projectId: "movie-hat-9c418",
  storageBucket: "movie-hat-9c418.appspot.com",
  messagingSenderId: "1061874698443",
  appId: "1:1061874698443:web:b2326dbc709a9237c2b34e"
};

initializeApp(firebaseConfig);

// localStorage can hold junk (a hand-cleared value, a stringified
// `undefined` from an old bug) — junk means "nothing remembered", not a
// crash before the app even mounts.
// How long a just-loaded hat stands in for a fresh read when the hat screen
// mounts (getHat's reuseFresh). Long enough to cover tapping a hat on the
// hats list; short enough that coming back later still reads anew.
const HAT_REUSE_MS = 30 * 1000;

function readLocal (key) {
  try {
    return JSON.parse(window.localStorage.getItem(key));
  } catch {
    return null;
  }
}

// The remembered current hat. Newer saves are a {title, hatKey} pair —
// the KEY is the hat's identity, because titles are display labels and two
// hats can share one. A legacy save is a bare title from before that was
// true; carrying it forward with no key makes getHat resolve it the old way
// once, after which it is re-saved in the new form.
function readDefaultHat () {
  const saved = readLocal('defaultMovieHat');
  if (saved?.title && saved?.hatKey) return saved;

  const legacyTitle = readLocal('defaultMovieHatTitle');
  return legacyTitle ? { title: legacyTitle, hatKey: null } : null;
}

// The last copy of the remembered hat this phone read, for this account
// (hatCache.js). Shown from the first render; getHat still reads.
const cachedHat = readHatCache(window.localStorage, {
  email: readLocal('movieHatEmail'),
  hatKey: readDefaultHat()?.hatKey
});

// Keeps the phone's copy in step with the hat on screen: after a read, an
// add, a draw. Saved a beat after the last change, and only once the store
// says which hat the data is from (hatLoaded) and that it is still the
// current one - while a switch to another hat is mid-read, the old hat's
// movies are on screen under the new hat's key, and that must not be saved.
function hatCachePlugin (store) {
  const WATCHED = ['setMovieHat', 'setHistory', 'setMembers', 'setMirrorFeedKey', 'setHatLoaded'];
  let timer = null;
  store.subscribe((mutation) => {
    if (!WATCHED.includes(mutation.type)) return;
    clearTimeout(timer);
    timer = setTimeout(() => {
      const state = store.state;
      if (!state.hatLoaded || state.hatLoaded.hatKey !== state.dbKeyForHatTitle) return;
      writeHatCache(window.localStorage, {
        email: state.email,
        hatKey: state.dbKeyForHatTitle,
        title: state.movieHatTitle,
        movies: state.movieHat,
        history: state.history,
        members: state.members,
        mirrorFeedKey: state.mirrorFeedKey
      });
    }, 300);
  });
}

export default createStore({
  plugins: [hatCachePlugin],
  state: {
    // Restored here, at creation, so every component sees the remembered
    // values from its first render — this used to be scattered across
    // Login.vue and Hat.vue mounted hooks, and any route that mounted first
    // (a refresh on /drawn-movie) saw nulls.
    email: readLocal('movieHatEmail'),
    // Whether Firebase itself says we're signed in, as opposed to an email
    // remembered in localStorage. Null until onAuthStateChanged first fires.
    //
    // These were the same thing until 2026-08-16, which was the problem: the
    // app trusted `movieHatEmail` in localStorage and never checked the
    // session behind it. That works only while the database lets anyone
    // read and write. The moment the rules require a signed-in user, a
    // remembered email with no live session means the app looks logged in
    // and every request fails.
    authUser: null,
    authResolved: false,
    name: readLocal('movieHatName'),
    movieHat: cachedHat?.movies || null,
    history: cachedHat?.history || null,
    members: cachedHat?.members || null,
    movieHatTitle: readDefaultHat()?.title || null,
    dbKeyForHatTitle: readDefaultHat()?.hatKey || null,
    drawnMovie: null,
    // True between tapping Draw and the reveal on the drawn-movie screen —
    // that screen plays the hat animation while this is set.
    drawRevealPending: false,
    movieChoices: null,
    // A human-readable problem the UI should show. Failures used to go only
    // to the console, which is how an outage looked like an empty app.
    appError: null,
    // A newer deploy exists than the bundle this page is running. App.vue
    // applies it at a quiet moment; the banner is the fallback.
    updateAvailable: false,
    // The deployed bundle that update is for, so a second Refresh for the
    // SAME version can stop trusting the service worker (utils/appUpdate.js).
    updateTargetBundle: null,
    // App.vue is applying the update right now: the banner says so instead
    // of offering a Refresh.
    updateApplying: false,
    // The connection couldn't carry the new version, so this one stays put.
    updateDeferred: false,

    // Secret path segment for the current hat's Magic Mirror feed, or null if
    // the feed has never been turned on for it. Per-hat: getHat sets it from
    // whichever hat it just loaded.
    mirrorFeedKey: cachedHat?.mirrorFeedKey || null,

    // Which hat the fields above were last filled from, and when. Tapping a
    // hat on the hats list hands over the copy that list just read, and the
    // hat screen mounting right after would otherwise read it all again —
    // Sentry flagged that as an N+1 on /hat-list (2026-10-18).
    hatLoaded: null,

    // This account's `siteUsers/<uid>` row, or null — the movie-request
    // permission shared with the standalone Movie Requests app
    // (request.movie-hat.com). See utils/siteAccess.js.
    //
    // Null means one of two very different things, which is why
    // `siteUserResolved` exists beside it: nobody has read it yet, or it was
    // read and there is no row. Almost everybody who opens Movie Hat is in
    // the second case and always will be — a family member in a hat has no
    // reason to have one — so the request and access screens stay invisible
    // rather than flickering into view while this resolves.
    siteUser: null,
    siteUserResolved: false
  },
  getters: {
    isDevHat: (state) => {
      return state.movieHatTitle === 'Dev Hat';
    },
    // The hat on screen is the database's answer for the current hat, not
    // the phone's copy from last time (hatCache.js). A draw waits for this:
    // drawing from an old copy could pick a movie someone else already drew.
    hatIsFresh: (state) => Boolean(state.hatLoaded && state.hatLoaded.hatKey === state.dbKeyForHatTitle),
    // "May this account ask the Mac mini for a download?" — the single place
    // the app asks, so the Request button, the request screen and the header
    // pill can never disagree. Two ways to qualify: the hard-coded three in
    // owner.mjs, or an approved `siteUsers` row. The database rules enforce
    // exactly the same pair; this only decides what to render.
    mayRequestMovies: (state) => mayRequestMovies(
      siteUserState({ email: state.email, row: state.siteUser })
    ),
    // "May this account see who is waiting and decide?" Matt, or anyone he
    // has made an admin. Everyone else never learns the screen exists.
    mayApproveAccess: (state) => mayApprove({ row: state.siteUser, email: state.email })
  },
  mutations: {
    setAuthUser (state, user) {
      state.authUser = user;
      state.authResolved = true;
    },
    setEmail (state, value) {
      if (value == null) {
        window.localStorage.removeItem('movieHatEmail');
      } else {
        window.localStorage.setItem('movieHatEmail', JSON.stringify(value));
      }
      state.email = value;
    },
    setName (state, value) {
      if (value == null) {
        window.localStorage.removeItem('movieHatName');
      } else {
        window.localStorage.setItem('movieHatName', JSON.stringify(value));
      }
      state.name = value;
    },
    setMovieHat (state, value) {
      state.movieHat = value;
    },
    setSiteUser (state, row) {
      state.siteUser = row || null;
      state.siteUserResolved = true;
    },
    /**
     * The current hat, as the {title, hatKey} PAIR. The key is the identity
     * — titles are labels and two hats can share one — so the two are set
     * and remembered together. Pass null to forget the current hat.
     */
    setCurrentHat (state, hat) {
      // The legacy title-only save is superseded either way.
      window.localStorage.removeItem('defaultMovieHatTitle');

      if (hat?.title && hat?.hatKey) {
        window.localStorage.setItem('defaultMovieHat', JSON.stringify({ title: hat.title, hatKey: hat.hatKey }));
      } else {
        window.localStorage.removeItem('defaultMovieHat');
      }

      state.movieHatTitle = hat?.title || null;
      state.dbKeyForHatTitle = hat?.hatKey || null;
    },
    setHatLoaded (state, value) {
      state.hatLoaded = value;
    },
    setDbKeyForHatTitle (state, value) {
      state.dbKeyForHatTitle = value;
    },
    setHistory (state, value) {
      state.history = value;
    },
    setMembers (state, value) {
      state.members = value;
    },
    setDrawnMovie (state, value) {
      state.drawnMovie = value;
    },
    setDrawRevealPending (state, value) {
      state.drawRevealPending = Boolean(value);
    },
    setMovieChoices (state, value) {
      state.movieChoices = value;
    },
    setAppError (state, value) {
      state.appError = value;
    },
    setMirrorFeedKey (state, value) {
      state.mirrorFeedKey = value || null;
    },
    setUpdateAvailable (state, value) {
      state.updateAvailable = Boolean(value);
    },
    setUpdateTargetBundle (state, value) {
      state.updateTargetBundle = value || null;
    },
    setUpdateApplying (state, value) {
      state.updateApplying = Boolean(value);
    },
    setUpdateDeferred (state, value) {
      state.updateDeferred = Boolean(value);
    }
  },
  actions: {
    /**
     * Subscribe to the real session. Called once at start-up.
     *
     * When Firebase says there IS a user we adopt its email, so the two can
     * never disagree. When it says there is NOT, the remembered email is
     * left in place for now — the rules are still open, so the app keeps
     * working — but `authUser` stays null, which is what the sign-in gate
     * will look at once the rules are closed.
     */
    watchAuth (context) {
      onAuthStateChanged(getAuth(), (user) => {
        // The phone's copy of the hat is one account's. Signed out, or
        // somebody else signed in: throw it away, and take it off the screen
        // if nothing fresher has replaced it yet.
        const copyFor = hatCacheEmail(window.localStorage);
        if (copyFor && copyFor !== user?.email) {
          clearHatCache(window.localStorage);
          if (!context.state.hatLoaded) {
            context.commit('setMovieHat', null);
            context.commit('setHistory', null);
            context.commit('setMembers', null);
            context.commit('setMirrorFeedKey', null);
          }
        }

        context.commit('setAuthUser', user || null);

        if (user?.email) {
          context.commit('setEmail', user.email);
          if (user.displayName) context.commit('setName', user.displayName);
          context.dispatch('loadSiteUser', user);
        } else {
          context.commit('setSiteUser', null);
        }
      });
    },
    /**
     * Read this account's `siteUsers/<uid>` row — the movie-request
     * permission, shared with the standalone Movie Requests app.
     *
     * READ, not read-or-create, and that asymmetry is the point. In Movie
     * Requests signing in IS asking to be let in, so it writes a pending row
     * for anyone who lacks one. Here, signing in is just using Movie Hat:
     * most people who do are family members in a hat who have never heard of
     * any of this, and writing them a pending row would fill Matt's waiting
     * list with people who never asked for anything.
     *
     * The one exception is Matt's own bootstrap row. He needs to be an admin
     * to see the waiting list at all, and the rules allow exactly that one
     * write for his Google-verified address — so doing it here means he
     * never has to open the other app to become one.
     *
     * Failure is not an error anybody should see: no row is the normal case,
     * and a refused read just means the screens stay hidden.
     */
    async loadSiteUser (context, user) {
      if (!user?.uid) {
        context.commit('setSiteUser', null);
        return null;
      }

      let row = null;
      try {
        row = await dbGet(siteUserPath(user.uid));
      } catch (error) {
        console.warn('Could not read the site user row', error);
      }

      if (!row && isOwner(user.email)) {
        try {
          const bootstrap = buildSiteUser({
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL
          });
          await dbPut(siteUserPath(user.uid), bootstrap);
          row = { ...bootstrap, requestedAt: Date.now() };
        } catch (error) {
          console.warn('Could not write the owner bootstrap row', error);
        }
      }

      context.commit('setSiteUser', row);
      return row;
    },
    async login (context) {
      const auth = getAuth();
      const provider = new GoogleAuthProvider();

      try {
        const result = await signInWithPopup(auth, provider);
        // const token = result.user.stsTokenManager.accessToken; // This is the Google API access token.
        // const user = result.user; // The signed-in user info.

        // Handle the result.
        if (result) {
          const userData = result.user;
          console.log('userData: ', userData);
          context.commit('setEmail', userData.email);

          if (userData.displayName) {
            context.commit('setName', userData.displayName);
          }
        }
        return null;
      } catch (error) {
        // A closed window or a second tap is a choice, not a bug for Sentry
        // (utils/signIn.js). A blocked window is the visitor's to fix, so it
        // comes back as a sentence for the sign-in screen.
        if (isSignInDismissal(error)) return null;
        const message = signInMessage(error);
        if (!message) console.error(error);
        return message;
      }
    },
    /**
     * Automated-testing sign-in (Cinema Roll's Login.vue testToken pattern).
     * Only tokens minted by the Admin SDK (yarn mint-hat-token) can pass
     * signInWithCustomToken, so this cannot reach a real account.
     */
    async loginWithTestToken (context, token) {
      const result = await signInWithCustomToken(getAuth(), token);
      // watchAuth adopts the email too, but not before the caller wants to
      // navigate — commit it now so the first getHat has it.
      if (result?.user?.email) {
        context.commit('setEmail', result.user.email);
        if (result.user.displayName) context.commit('setName', result.user.displayName);
      }
    },
    /**
     * Load the current hat. `snapshot` is the hat as a caller already read it
     * (the hats list) — used instead of a read when it is for this hat.
     * `reuseFresh` skips the read when this hat was loaded moments ago, which
     * is what the hat screen asks for on mount; everyone else (after a draw,
     * an edit) wants the database's answer and calls it bare.
     */
    async getHat (context, { snapshot = null, reuseFresh = false } = {}) {
      const title = context.state.movieHatTitle;
      if (!title) {
        return;
      }

      const loaded = context.state.hatLoaded;
      if (
        reuseFresh &&
        loaded &&
        loaded.hatKey === context.state.dbKeyForHatTitle &&
        Date.now() - loaded.at < HAT_REUSE_MS
      ) {
        return;
      }

      // The stored key IS the identity; resolving by title is only for a
      // legacy title-only save, and its answer gets re-saved as the pair so
      // resolution happens at most once.
      let dbKey = context.state.dbKeyForHatTitle;
      if (!dbKey) {
        dbKey = await resolveHatKey(title, context.state.email);
        if (dbKey) context.commit('setCurrentHat', { title, hatKey: dbKey });
      }

      if (!dbKey) {
        // The remembered hat opens nothing — deleted, or we're not a member.
        // Forgetting it means the next load goes to the hat list instead of
        // silently showing nothing forever.
        context.commit('setAppError', `Couldn't open "${title}" — it may have been deleted, or you may no longer be a member.`);
        context.commit('setCurrentHat', null);
        context.commit('setMovieHat', []);
        context.commit('setHistory', []);
        return;
      }

      let data = null;
      try {
        data = snapshot && snapshot.subKey === dbKey
          ? snapshot
          : await dbGet(hatPath(title, dbKey));
      } catch (error) {
        if (error.status === 401 || error.status === 403) {
          // Refused outright — signed out, or no longer a member. Forget
          // the remembered hat so this can't recur on every load.
          context.commit('setAppError', `Couldn't open "${title}" — you may need to sign in again, or you may no longer be a member.`);
          context.commit('setCurrentHat', null);
          clearHatCache(window.localStorage);
        } else {
          context.commit('setAppError', `Couldn't load "${title}". Please try again.`);
        }
        return;
      }

      if (data) {
        let hatAsArray = [];

        if (data.movies) {
          hatAsArray = Object.keys(data.movies).map((key) => {
            const movie = { ...data.movies[key], dbKey: key };
            return movie;
          });
        }

        let history = [];

        if (data.history) {
          history = Object.keys(data.history).map((key) => {
            const movie = { ...data.history[key], dbKey: key };
            return movie;
          });
        }

        context.commit('setAppError', null);
        context.commit('setMembers', data.members);
        context.commit('setMovieHat', hatAsArray);
        context.commit('setHistory', history);
        context.commit('setMirrorFeedKey', data.mirrorFeedKey);
        context.commit('setHatLoaded', { hatKey: dbKey, at: Date.now() });
        context.dispatch('publishMirrorFeedIfStale');
      }
    },

    // ------------------------------------------------------------------
    // Magic Mirror feed. See src/assets/javascript/mirrorFeed.js for why this
    // exists: the mirror used to read a whole hat over unauthenticated REST,
    // which the membership rules closed.
    //
    // The secret must never live in Movie Hat's public bundle, so it is
    // generated per-hat at runtime and stored on the hat itself, where only
    // its members can read it.
    //
    // Both actions take an explicit hat and fall back to the CURRENT one, so
    // the draw path can call them bare while the hats list — which holds every
    // hat you belong to, none of them necessarily current — passes its own.
    async ensureMirrorFeedKey (context, { title, hatKey, existingKey } = {}) {
      const hatTitle = title || context.state.movieHatTitle;
      const dbKey = hatKey || context.state.dbKeyForHatTitle;
      const isCurrentHat = dbKey === context.state.dbKeyForHatTitle;
      if (!hatTitle || !dbKey) return null;

      const existing = existingKey || (isCurrentHat ? context.state.mirrorFeedKey : null);
      if (typeof existing === 'string' && existing.length >= 16) return existing;

      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      const key = Array.from(bytes).map((byte) => byte.toString(16).padStart(2, '0')).join('');

      // PATCH, not PUT: the hat's .validate insists every write leaves
      // memberEmails in place, and a PUT here would replace the whole hat.
      await dbPatch(hatPath(hatTitle, dbKey), { mirrorFeedKey: key });
      if (isCurrentHat) context.commit('setMirrorFeedKey', key);

      return key;
    },

    async publishMirrorFeed (context, { title, hatKey, secret, history } = {}) {
      const hatTitle = title || context.state.movieHatTitle;
      const dbKey = hatKey || context.state.dbKeyForHatTitle;
      const isCurrentHat = dbKey === context.state.dbKeyForHatTitle;
      const feedKey = secret || (isCurrentHat ? context.state.mirrorFeedKey : null);
      if (!hatTitle || !dbKey || !feedKey) return;

      const feed = buildMirrorFeed(history || (isCurrentHat ? context.state.history : null));

      try {
        // Keyed by title AND hatKey, mirroring the hats path, because that is
        // the only way the rules can reach this hat's memberEmails to decide
        // whether the writer belongs to it.
        await dbPut(`mirrorFeed/${encodeURIComponent(hatTitle)}/${dbKey}/${feedKey}`, feed);
      } catch (error) {
        // A mirror showing last week's pick is not worth interrupting a draw.
        console.warn('Could not publish the mirror feed', error);
      }
    },

    // Republish on load, at most every six hours. Drawing republishes
    // immediately — that is the event this feed exists to carry — so this only
    // covers a feed drifting out of date for other reasons.
    async publishMirrorFeedIfStale (context) {
      if (!context.state.mirrorFeedKey) return;

      const stamp = `movieHat.mirrorFeed.lastPublish.${context.state.dbKeyForHatTitle}`;
      const last = Number(window.localStorage.getItem(stamp) || 0);
      if (Date.now() - last < 6 * 60 * 60 * 1000) return;

      // Stamped BEFORE the publish, not after. A hat whose publish keeps
      // failing would otherwise retry on every getHat for the whole session.
      window.localStorage.setItem(stamp, String(Date.now()));
      await context.dispatch('publishMirrorFeed');
    }
  },
  modules: {
  }
})
